import { supabase } from "../../services/SupabaseService";
import { getCurrentUserId } from "../../utils/helpers";
import { Translation } from "../../entities/Translation";
import { Term } from "../../entities/Term";
import { fetchAllRows } from "../../utils/batching";

export interface TermWithTranslation {
  term: Term;
  translation: Translation | null;
}

class TranslationsRepository {
  private getUserId(): string {
    const id = getCurrentUserId();
    if (!id) throw new Error("User not authenticated");
    return id;
  }

  /** Loads all active translations of a project for one language, in pages (a single request returns at most 1000 rows).
   *  Filters on the project through the terms table, so no list of term IDs has to be sent in the URL. */
  async getByProjectAndLanguage(projectId: string, languageCode: string): Promise<Translation[]> {
    const userId = this.getUserId();
    const normalizedCode = languageCode.toLowerCase();
    const rows = await fetchAllRows((from, to) =>
      supabase
        .from("translations")
        .select("*, terms!inner(project_id)", { count: "exact" })
        .eq("terms.project_id", projectId)
        .is("terms.deleted_at", null)
        .eq("language_code", normalizedCode)
        .eq("user_id", userId)
        .is("deleted_at", null)
        .order("id", { ascending: true })
        .range(from, to)
    );
    return rows.map(Translation.fromRow);
  }

  /** Load all active translations for a single term across all languages. */
  async getByTermId(termId: string): Promise<Translation[]> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("translations")
      .select("*")
      .eq("term_id", termId)
      .eq("user_id", userId)
      .is("deleted_at", null)
      .order("language_code", { ascending: true });

    if (error) throw new Error(error.message);
    return (data || []).map(Translation.fromRow);
  }

  /** Load all active translations for a project (for stats/progress computation). */
  async getByProject(projectId: string): Promise<Translation[]> {
    const userId = this.getUserId();

    // First, get all active term IDs for the project
    const { data: termsData, error: termsError } = await supabase
      .from("terms")
      .select("id")
      .eq("project_id", projectId)
      .eq("user_id", userId)
      .is("deleted_at", null);

    if (termsError) throw new Error(termsError.message);
    const termIds = (termsData || []).map((t) => t.id);
    if (termIds.length === 0) return [];

    const { data, error } = await supabase
      .from("translations")
      .select("*")
      .in("term_id", termIds)
      .eq("user_id", userId)
      .is("deleted_at", null);

    if (error) throw new Error(error.message);
    return (data || []).map(Translation.fromRow);
  }

  /** Fetch all active translations for a given list of term IDs (all languages). */
  async getForTermIds(termIds: string[]): Promise<Translation[]> {
    if (termIds.length === 0) return [];
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("translations")
      .select("*")
      .in("term_id", termIds)
      .eq("user_id", userId)
      .is("deleted_at", null);

    if (error) throw new Error(error.message);
    return (data || []).map(Translation.fromRow);
  }

  /** Paginated fetch for the language editor.
   *  "all" mode: paginates terms directly.
   *  "missingOnly" mode: resolves missing terms server-side (lightweight) then paginates client-side. */
  async getForLanguageEditorPaginated(
    projectId: string,
    languageCode: string,
    page: number,
    pageSize: number,
    options: { search?: string; missingOnly?: boolean } = {}
  ): Promise<{ rows: TermWithTranslation[]; total: number }> {
    const userId = this.getUserId();
    const normalizedCode = languageCode.toLowerCase();

    if (!options.missingOnly) {
      // ── "all" mode: paginate terms directly ──────────────────────────────
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let termsQuery = supabase
        .from("terms")
        .select("*", { count: "exact" })
        .eq("project_id", projectId)
        .eq("user_id", userId)
        .is("deleted_at", null)
        .order("key", { ascending: true })
        .range(from, to);

      if (options.search?.trim()) {
        termsQuery = termsQuery.ilike("key", `%${options.search.trim()}%`);
      }

      const { data: termsData, error: termsError, count } = await termsQuery;
      if (termsError) throw new Error(termsError.message);
      const terms = (termsData || []).map(Term.fromRow);

      if (terms.length === 0) return { rows: [], total: count ?? 0 };

      const termIds = terms.map((t) => t.id);
      const { data: transData, error: transError } = await supabase
        .from("translations")
        .select("*")
        .in("term_id", termIds)
        .eq("language_code", normalizedCode)
        .eq("user_id", userId)
        .is("deleted_at", null);

      if (transError) throw new Error(transError.message);

      const transMap = new Map((transData || []).map((tr) => [tr.term_id, Translation.fromRow(tr)]));
      return {
        rows: terms.map((term) => ({ term, translation: transMap.get(term.id) ?? null })),
        total: count ?? 0,
      };
    }

    // ── "missing only" mode — true server-side via RPC ───────────────────
    const { data: rpcData, error: rpcError } = await supabase.rpc("get_missing_terms_paginated", {
      p_project_id: projectId,
      p_language_code: normalizedCode,
      p_search: options.search?.trim() || undefined,
      p_page: page,
      p_page_size: pageSize,
    });

    if (rpcError) throw new Error(rpcError.message);
    const rpcRows = rpcData || [];
    if (rpcRows.length === 0) return { rows: [], total: 0 };

    const total = Number(rpcRows[0].total_count);
    const rows = rpcRows.map((r) => ({
      term: Term.fromRow(r),
      translation: r.translation_id
        ? Translation.fromRow({
            id: r.translation_id,
            term_id: r.id,
            language_code: normalizedCode,
            value_one: r.value_one,
            value_other: r.value_other,
            created_at: r.translation_created_at,
            deleted_at: null,
          })
        : null,
    }));
    return { rows, total };
  }

  /** Search translations by value (partial, case-insensitive) within a project.
   *  Filters on the project through the terms table, so no list of term IDs has to be sent in the URL. */
  async searchByValueInProject(projectId: string, query: string): Promise<Translation[]> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("translations")
      .select("*, terms!inner(project_id)")
      .eq("terms.project_id", projectId)
      .is("terms.deleted_at", null)
      .eq("user_id", userId)
      .is("deleted_at", null)
      .or(`value_one.ilike.%${query}%,value_other.ilike.%${query}%`);

    if (error) throw new Error(error.message);
    return (data || []).map(Translation.fromRow);
  }

  /** Creates several translations for one language in a single request. Either all of them are created, or none. */
  async createMany(
    languageCode: string,
    translations: { termId: string; valueOne: string; valueOther: string | null }[]
  ): Promise<Translation[]> {
    if (translations.length === 0) return [];
    const userId = this.getUserId();
    const normalizedCode = languageCode.toLowerCase();
    const { data, error } = await supabase
      .from("translations")
      .insert(
        translations.map((t) => ({
          term_id: t.termId,
          language_code: normalizedCode,
          value_one: t.valueOne,
          value_other: t.valueOther ?? null,
          user_id: userId,
        }))
      )
      .select();

    if (error) throw new Error(error.message);
    return (data || []).map(Translation.fromRow);
  }

  /** Updates the values of an existing translation. */
  async update(id: string, valueOne: string, valueOther: string | null): Promise<Translation> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("translations")
      .update({ value_one: valueOne, value_other: valueOther ?? null })
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Translation could not be updated.");
    return Translation.fromRow(data);
  }

  /** Creates or updates a translation for a term + language. Handles soft-deleted rows. */
  async upsert(termId: string, languageCode: string, valueOne: string, valueOther: string | null): Promise<Translation> {
    const userId = this.getUserId();
    const normalizedCode = languageCode.toLowerCase();

    // Check for an existing active translation
    const { data: existing, error: selectError } = await supabase
      .from("translations")
      .select("*")
      .eq("term_id", termId)
      .eq("language_code", normalizedCode)
      .eq("user_id", userId)
      .is("deleted_at", null)
      .maybeSingle();

    if (selectError) throw new Error(selectError.message);

    if (existing) return this.update(existing.id, valueOne, valueOther);

    // Insert new row (previous soft-deleted rows remain; partial index allows this)
    const { data: inserted, error: insertError } = await supabase
      .from("translations")
      .insert({
        term_id: termId,
        language_code: normalizedCode,
        value_one: valueOne,
        value_other: valueOther ?? null,
        user_id: userId,
      })
      .select()
      .single();

    if (insertError) throw new Error(insertError.message);
    if (!inserted) throw new Error("Translation could not be created.");
    return Translation.fromRow(inserted);
  }
}

export default TranslationsRepository;
